import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SEARCH_CONFIG } from '../constants';
import { tasksService } from '../services/tasks.service';
import { AsanaTask, TaskSearchResult } from '../types';
import {
  TaskSearchOption,
  fetchAndSelectTask,
  getTaskSearchErrorMessage,
  getTaskSearchOptionId,
  toTaskSearchOption,
} from '../utils/task-lookup.utils';

interface UseTaskSearchReturn {
  searchQuery: string;
  searchResults: TaskSearchResult[];
  searchOptions: TaskSearchOption[];
  isSearching: boolean;
  /** True while the selected result's full task is being fetched. */
  isFetchingSelection: boolean;
  /** Search or selection failure message, if any. */
  errorMessage: string | null;
  handleSearch: (query: string) => void;
  handleSelect: (option: TaskSearchOption | null) => Promise<void>;
  resetSearch: () => void;
}

/**
 * Debounced job search by customer name. Selecting a result fetches the full task
 * from its platform (same path as fetch-by-ID) and only then calls onTaskSelect.
 * @param onTaskSelect - Receives the fully fetched task on successful selection
 */
export const useTaskSearch = (onTaskSelect: (task: AsanaTask) => void): UseTaskSearchReturn => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<TaskSearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [isFetchingSelection, setIsFetchingSelection] = useState(false);
  const [selectionError, setSelectionError] = useState<string | null>(null);
  const latestQueryRef = useRef('');
  const selectionInFlightRef = useRef(false);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const runSearch = useCallback(async (query: string): Promise<void> => {
    if (query.length < SEARCH_CONFIG.MIN_SEARCH_LENGTH) {
      setSearchResults([]);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    setSearchError(null);

    try {
      const results = await tasksService.getAllTasks(query);
      if (latestQueryRef.current === query) {
        setSearchResults(results);
      }
    } catch (error) {
      if (latestQueryRef.current === query) {
        console.error('Search error:', error);
        setSearchError(getTaskSearchErrorMessage(error));
        setSearchResults([]);
      }
    } finally {
      if (latestQueryRef.current === query) {
        setIsSearching(false);
      }
    }
  }, []);

  const handleSearch = useCallback((query: string): void => {
    setSearchQuery(query);
    setSelectionError(null);
    latestQueryRef.current = query;
    clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => runSearch(query), SEARCH_CONFIG.DEBOUNCE_DELAY);
  }, [runSearch]);

  const resetSearch = useCallback((): void => {
    clearTimeout(debounceTimerRef.current);
    latestQueryRef.current = '';
    setSearchQuery('');
    setSearchResults([]);
    setIsSearching(false);
    setSearchError(null);
    setSelectionError(null);
  }, []);

  useEffect(() => () => clearTimeout(debounceTimerRef.current), []);

  const handleSelect = async (option: TaskSearchOption | null): Promise<void> => {
    const result = option
      ? searchResults.find((item) => getTaskSearchOptionId(item) === option.id)
      : undefined;
    // One selection at a time: a second fetch could resolve later and overwrite the first.
    if (!result || selectionInFlightRef.current) return;

    selectionInFlightRef.current = true;
    setIsFetchingSelection(true);
    setSelectionError(null);
    try {
      setSelectionError(await fetchAndSelectTask(result.source, result.gid, onTaskSelect));
    } finally {
      selectionInFlightRef.current = false;
      setIsFetchingSelection(false);
    }
  };

  const searchOptions = useMemo(() => searchResults.map(toTaskSearchOption), [searchResults]);

  return {
    searchQuery,
    searchResults,
    searchOptions,
    isSearching,
    isFetchingSelection,
    errorMessage: searchError ?? selectionError,
    handleSearch,
    handleSelect,
    resetSearch,
  };
};
